import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  useEffect(() => {
    document.title = '廿日市の塾について｜学習塾NICK｜廿日市市大野の中学生塾・講師紹介';

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', '廿日市の塾・学習塾NICKについてのページ。廿日市市大野で20年以上の実績。元公立高校教員の講師が廿日市市の中学生一人ひとりに寄り添う指導。少人数指導・個別指導・自習室完備。塾の理念・講師紹介・指導スタイルをご紹介。');
    setMeta('keywords', '廿日市 塾 講師,廿日市 学習塾 評判,廿日市市大野 塾,廿日市 中学生 塾 おすすめ,学習塾NICK 講師,廿日市 塾 特徴');
    setMeta('og:title', '廿日市の塾について｜学習塾NICK｜廿日市市大野', true);
    setMeta('og:description', '廿日市市大野の学習塾NICK。元公立高校教員の講師が廿日市市の中学生に寄り添う指導。少人数・個別指導で成績アップと志望校合格をサポート。', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/about`, true);
    setMeta('twitter:title', '廿日市の塾について｜学習塾NICK');
    setMeta('twitter:description', '廿日市市大野の学習塾NICK。元公立高校教員の講師が廿日市市の中学生に寄り添う指導で成績アップをサポート。');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/about`);

    // Schema.org JSON-LD
    const schemaId = 'schema-about-page';
    let schemaEl = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!schemaEl) {
      schemaEl = document.createElement('script');
      schemaEl.id = schemaId;
      schemaEl.type = 'application/ld+json';
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "廿日市の塾について｜学習塾NICK",
      "description": "廿日市市大野の学習塾NICK。元公立高校教員の講師が廿日市市の中学生に寄り添う指導。少人数・個別指導で成績アップと志望校合格をサポート。",
      "url": `${import.meta.env.VITE_SITE_URL}/about`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "ホーム", "item": `${import.meta.env.VITE_SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "塾について", "item": `${import.meta.env.VITE_SITE_URL}/about` }
        ]
      },
      "mainEntity": {
        "@type": "EducationalOrganization",
        "name": "学習塾NICK",
        "description": "廿日市市大野で20年以上の実績を持つ中学生専門の学習塾。元公立高校教員による少人数・個別指導で成績アップと志望校合格をサポート。",
        "url": `${import.meta.env.VITE_SITE_URL}/`,
        "telephone": "+81-829-78-1638",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "大野中央四丁目４－１６",
          "addressLocality": "廿日市市",
          "addressRegion": "広島県",
          "postalCode": "739-0400",
          "addressCountry": "JP"
        },
        "employee": [
          {
            "@type": "Person",
            "name": "新田 祐樹",
            "jobTitle": "代表講師・塾長",
            "description": "公立高校で10年間、進路指導や入試業務に携わってきた経験豊富な講師。廿日市市の中学生の志望校合格を全力でサポート。"
          }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "指導スタイル",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "集団講座指導" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "少人数指導" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "完全個別指導" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "自習室の活用" } }
          ]
        }
      },
      "mainEntityOfPage": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "体験授業は何回受けられますか？",
            "acceptedAnswer": { "@type": "Answer", "text": "無料体験授業は1回受けていただけます。実際の授業の雰囲気や指導方法を体感していただき、ご納得いただいた上で入塾をご検討ください。" }
          },
          {
            "@type": "Question",
            "name": "途中でコースを変更できますか？",
            "acceptedAnswer": { "@type": "Answer", "text": "はい、可能です。学習状況や目標の変化に応じて、コースの変更をご相談いただけます。" }
          },
          {
            "@type": "Question",
            "name": "定期テスト前だけの受講はできますか？",
            "acceptedAnswer": { "@type": "Answer", "text": "定期テスト対策のみの短期受講も可能です。詳しくはお問い合わせください。" }
          },
          {
            "@type": "Question",
            "name": "保護者面談はありますか？",
            "acceptedAnswer": { "@type": "Answer", "text": "はい、定期的に保護者面談を実施しています。学習状況や今後の方針について、詳しくお話しさせていただきます。" }
          }
        ]
      }
    });

    return () => {
      document.title = '廿日市の塾なら学習塾NICK｜廿日市市の中学生塾・成績アップ・高校受験対策';
      if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/`);
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, []);

  const philosophy = [
    {
      title: '一人ひとりに寄り添う指導',
      description: '生徒の個性や学習状況を理解し、それぞれに最適な指導方法を提供します。画一的な指導ではなく、一人ひとりの可能性を最大限に引き出すことを大切にしています。',
      icon: 'ri-user-heart-line'
    },
    {
      title: '学ぶ楽しさを伝える',
      description: 'わかる喜び、できる達成感を通じて、学ぶことの楽しさを実感してもらいます。勉強が苦手な生徒も、少しずつ自信をつけていけるようサポートします。',
      icon: 'ri-emotion-happy-line'
    },
    {
      title: '自立した学習者を育てる',
      description: '答えを教えるだけでなく、自分で考え、問題を解決する力を養います。将来にわたって役立つ、自ら学ぶ姿勢を身につけてもらうことを目指しています。',
      icon: 'ri-lightbulb-line'
    },
    {
      title: '保護者との信頼関係',
      description: '定期的な面談や報告を通じて、保護者様と密にコミュニケーションを取ります。ご家庭と連携しながら、お子様の成長をサポートします。',
      icon: 'ri-parent-line'
    }
  ];

  const teachers = [
    {
      name: '塾長 新田 祐樹',
      nickname: 'NICK(ニック)',
      role: '代表講師',
      experience: '公立高校教員歴10年',
      specialties: ['数学', '全教科可能', '進路指導', '高校受験対策'],
      message: '公立高校で10年間、進路指導や入試業務に携わってきました。その経験を活かし、一人ひとりの生徒に最適な学習プランを提案します。廿日市市の中学生の皆さんの夢の実現を、全力でサポートします。',
      image: 'https://static.readdy.ai/image/58c223b9eef17a4b88bac47f4e33252a/ad20518c3a2816d56ad3093932ea9584.jpeg'
    },
    {
      name: '中村 先生',
      nickname: '',
      role: 'AGITO(アギト)',
      experience: '塾講師歴３年',
      specialties: ['理科', '数学', '全教科指導', '基礎学力定着'],
      message: 'スタディコースとサポートコースのいくつかを担当しています。基礎からしっかり理解できるよう、丁寧に指導します。勉強が苦手な生徒も、一歩ずつ着実に力をつけていけるようサポートします。一緒に頑張りましょう。',
      image: 'https://public.readdy.ai/ai/img_res/eb60133f273b72bceb7a7b1316affe0f.jpg'
    }
  ];

  const teachingStyle = [
    {
      title: '集団講座指導',
      description: '数学強化コースで実施。学年別のクラス編成で、入試頻出単元を重点的に学習します。仲間と一緒に学ぶことで、競争意識と協調性を育みます。',
      features: ['学年別クラス編成', '入試頻出単元重点学習', '質の高い解説授業', '仲間と切磋琢磨']
    },
    {
      title: '少人数指導',
      description: '4〜8名の少人数クラスで、一人ひとりに目が届く指導を実現。生徒同士が刺激し合いながら、切磋琢磨できる環境を提供します。',
      features: ['質問しやすい雰囲気', '個別の理解度確認', '適度な競争意識', '仲間との学び合い']
    },
    {
      title: '完全個別指導',
      description: 'マンツーマンで、生徒のペースに合わせた指導。苦手分野の克服や、得意分野のさらなる伸長に最適です。',
      features: ['オーダーメイドカリキュラム', '柔軟な授業進度', '徹底的な弱点克服', '集中的な質問対応']
    },
    {
      title: '自習室の活用',
      description: '開校時間内はいつでも利用可能な自習室を完備。集中して学習できる環境で、自主学習の習慣を身につけます。',
      features: ['静かな学習環境', '質問対応可能', '学習計画のサポート', '無料で利用可能']
    }
  ];

  const atmosphere = [
    {
      title: 'アットホームな雰囲気',
      description: '生徒と講師の距離が近く、何でも相談できる環境です。勉強のことだけでなく、学校生活の悩みも気軽に話せます。'
    },
    {
      title: '清潔で明るい教室',
      description: '明るく清潔な教室で、集中して学習できます。最新の設備を整え、快適な学習環境を提供しています。'
    },
    {
      title: '安心・安全への配慮',
      description: '保護者様ともまめな連携を行います。また専用駐車場完備で、送迎も安心です。'
    }
  ];

  const faqs = [
    {
      question: '体験授業は何回受けられますか？',
      answer: '無料体験授業は1回受けていただけます。実際の授業の雰囲気や指導方法を体感していただき、ご納得いただいた上で入塾をご検討ください。'
    },
    {
      question: '途中でコースを変更できますか？',
      answer: 'はい、可能です。学習状況や目標の変化に応じて、コースの変更をご相談いただけます。最適な学習環境を提供できるよう、柔軟に対応いたします。'
    },
    {
      question: '定期テスト前だけの受講はできますか？',
      answer: '定期テスト対策のみの短期受講も可能です。詳しくはお問い合わせください。'
    },
    {
      question: '保護者面談はありますか？',
      answer: 'はい、定期的に保護者面談を実施しています。学習状況や今後の方針について、詳しくお話しさせていただきます。'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-32 bg-gradient-to-br from-[#4a90e2]/10 via-white to-[#4a90e2]/5">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-[#1a2332] mb-6">
              塾について
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              <strong>廿日市市大野</strong>で地域に根ざした学習塾として、<strong>中学生</strong>の<strong>成績アップ</strong>と<strong>志望校合格</strong>を全力でサポートしています。
            </p>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              塾の理念
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              学習塾NICKが大切にしている4つの理念
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {philosophy.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#f8f9fa] rounded-3xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-[#4a90e2]/10 rounded-2xl flex items-center justify-center mb-6">
                    <i className={`${item.icon} text-3xl text-[#4a90e2]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-4">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-16 bg-[#1a2332] rounded-3xl p-10 text-white text-center">
              <h3 className="text-2xl font-bold mb-6">
                私たちの使命
              </h3>
              <p className="text-white/80 text-lg leading-relaxed max-w-4xl mx-auto">
                <strong>廿日市市</strong>の<strong>中学生</strong>一人ひとりが、自分の可能性を信じ、目標に向かって努力できるようサポートすること。それが学習塾NICKの使命です。<strong>成績アップ</strong>や<strong>志望校合格</strong>はもちろん、将来にわたって役立つ学ぶ力を育てます。
              </p>
            </div>
          </div>
        </section>

        {/* Teachers Section */}
        <section className="py-24 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              講師紹介
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              経験豊富な講師陣が、一人ひとりの成長をサポートします
            </p>

            <div className="space-y-12">
              {teachers.map((teacher, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <img
                      src={teacher.image}
                      alt={teacher.name}
                      className="w-full max-w-md mx-auto h-[500px] object-cover rounded-3xl shadow-xl"
                    />
                  </div>

                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="bg-white rounded-3xl p-8">
                      <div className="mb-6">
                        <h3 className="text-3xl font-bold text-[#1a2332] mb-2">
                          {teacher.name}
                        </h3>
                        {teacher.nickname && (
                          <p className="text-xl text-[#4a90e2] font-semibold mb-2">
                            {teacher.nickname}
                          </p>
                        )}
                        <p className="text-lg text-[#4a90e2] font-semibold mb-2">
                          {teacher.role}
                        </p>
                        <p className="text-gray-600">{teacher.experience}</p>
                      </div>

                      <div className="mb-6">
                        <h4 className="font-bold text-[#1a2332] mb-3">専門分野</h4>
                        <div className="flex flex-wrap gap-2">
                          {teacher.specialties.map((specialty, idx) => (
                            <span
                              key={idx}
                              className="bg-[#4a90e2]/10 text-[#4a90e2] px-4 py-2 rounded-full text-sm font-semibold"
                            >
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-bold text-[#1a2332] mb-3">メッセージ</h4>
                        <p className="text-gray-600 leading-relaxed">
                          {teacher.message}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Teaching Style Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              指導スタイル
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              ハイブリッド型の指導を展開し、一人ひとりに最適な学習環境を提供します
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {teachingStyle.map((style, index) => (
                <div
                  key={index}
                  className="bg-[#f8f9fa] rounded-3xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <h3 className="text-2xl font-bold text-[#1a2332] mb-6">
                    {style.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {style.description}
                  </p>
                  <ul className="space-y-3">
                    {style.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <i className="ri-check-line text-xl text-[#4a90e2]"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Atmosphere Section */}
        <section className="py-24 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              塾の雰囲気
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              学習塾NICKの特徴的な環境
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {atmosphere.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-[#4a90e2]/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                    <i className="ri-home-heart-line text-3xl text-[#4a90e2]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-4">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="w-full h-[500px] rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=bright%20modern%20japanese%20classroom%20interior%20clean%20minimalist%20design%20natural%20lighting%20educational%20environment%20peaceful%20atmosphere%20study%20desks%20organized%20space&width=1200&height=600&seq=classroom001&orientation=landscape"
                alt="教室の様子"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              よくあるご質問
            </h2>
            <p className="text-gray-600 text-center mb-16">
              学習塾NICKについてのよくある質問
            </p>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-[#f8f9fa] rounded-2xl p-8"
                >
                  <h3 className="text-lg font-bold text-[#1a2332] mb-4 flex items-start gap-3">
                    <i className="ri-question-line text-2xl text-[#4a90e2] flex-shrink-0"></i>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed pl-11">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-[#1a2332]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              まずは無料体験からお気軽にご相談ください
            </h2>
            <p className="text-white/80 text-lg mb-10 leading-relaxed">
              <strong>廿日市市大野</strong>の学習塾NICKでは、<strong>無料体験授業</strong>を随時受け付けております。塾の雰囲気や指導方法を実際に体感していただき、ご納得いただいた上で入塾をご検討ください。<strong>廿日市市</strong>、<strong>宮島</strong>、<strong>阿品</strong>、<strong>大竹市</strong>など周辺地域からもお気軽にお越しください。
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/trial#trial-form-section"
                className="bg-[#4a90e2] text-white px-10 py-5 rounded-full text-lg font-semibold hover:bg-[#3a7bc8] transition-all hover:scale-105 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                無料体験に申し込む
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
              <a
                href="tel:0829-78-1638"
                className="bg-white text-[#1a2332] px-10 py-5 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                電話で問い合わせる
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
